import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  AssignV3Strategy,
  type AssignV3ActionMode,
  type AssignV3ActorAssigneeType,
} from "@redotech/redo-model/advanced-flow/rules/actions";
import type { AssignV3Action as AssignV3ActionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { GetUser } from "@redotech/redo-model/user";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { catchApiErrors } from "../../../../../../support-v2/db/error-handling";

type ModeTag = AssignV3ActionMode["_tag"];

const modeOptions = [
  {
    id: "specific_users" as const,
    type: "text" as const,
    text: "Specific users",
    value: "specific_users" as ModeTag,
  },
  {
    id: "user_group" as const,
    type: "text" as const,
    text: "User group",
    value: "user_group" as ModeTag,
  },
  {
    id: "actor" as const,
    type: "text" as const,
    text: "Trigger actor",
    value: "actor" as ModeTag,
  },
  {
    id: "unassign" as const,
    type: "text" as const,
    text: "Unassign",
    value: "unassign" as ModeTag,
  },
];

const ACTOR_ASSIGNEE_TYPE_LABEL: Record<AssignV3ActorAssigneeType, string> = {
  USER: "Human agent",
  AI: "AI agent",
};

const actorAssigneeTypeOptions: BadgeRedoListItem<AssignV3ActorAssigneeType>[] =
  (Object.keys(ACTOR_ASSIGNEE_TYPE_LABEL) as AssignV3ActorAssigneeType[]).map(
    (value) => ({
      id: value,
      type: "badge" as const,
      value,
      badge: { text: ACTOR_ASSIGNEE_TYPE_LABEL[value] },
    }),
  );

const strategyOptions = [
  {
    id: AssignV3Strategy.ROUND_ROBIN,
    type: "text" as const,
    text: "Round robin",
    value: AssignV3Strategy.ROUND_ROBIN,
  },
  {
    id: AssignV3Strategy.BALANCED,
    type: "text" as const,
    text: "Balanced (fewest open tickets)",
    value: AssignV3Strategy.BALANCED,
  },
];

export const AssignV3Action = memo(function AssignV3Action({
  action,
  onChange,
  editableFields,
}: {
  action: AssignV3ActionType;
  onChange: (updated: AssignV3ActionType) => void;
  /**
   * When omitted, all fields are editable (normal rule authoring). When
   * provided, only fields whose name is in the set are editable; others
   * render read-only. Used by the default-rule edit page to honour the
   * rule's template bindings.
   */
  editableFields?: ReadonlySet<string>;
}) {
  const team = useRequiredContext(TeamContext);
  const effectApi = useRequiredContext(EffectApiClientContext);

  const isEditable = (field: string) =>
    editableFields === undefined || editableFields.has(field);
  const modeEditable = isEditable("mode");
  const strategyEditable = isEditable("strategy");
  const maxTicketsEditable = isEditable("maxTicketsPerUser");

  const userOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (team?.users || []).map((teamUser) => {
      const user = teamUser.user as GetUser;
      return {
        id: user._id,
        type: "badge" as const,
        value: user._id,
        badge: { text: user.name || user._id },
      };
    });
  }, [team?.users]);

  const userGroupsLoad = useLoad(async () => {
    const client = effectApi.getClient();
    const result = await effectApi.runPromise(
      catchApiErrors(client.userGroup.listUserGroups({})),
    );
    return result.groups;
  }, [effectApi]);

  const groupOptions = useMemo(() => {
    const groups = userGroupsLoad.value ?? [];
    return groups.map((group) => ({
      id: group.id,
      type: "text" as const,
      text: group.name,
      value: group.id,
    }));
  }, [userGroupsLoad.value]);

  // Multi-select dropdown wiring
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedUsers: BadgeRedoListItem<string>[] = useMemo(() => {
    if (action.mode._tag !== "specific_users") {
      return [];
    }
    const selected = new Set(action.mode.mongoUserIds);
    return userOptions.filter((opt) => selected.has(opt.value));
  }, [action.mode, userOptions]);

  const selectedModeOption = modeOptions.find(
    (opt) => opt.value === action.mode._tag,
  );
  const selectedStrategyOption = strategyOptions.find(
    (opt) => opt.value === action.strategy,
  );
  const selectedGroupOption = useMemo(() => {
    const mode = action.mode;
    if (mode._tag !== "user_group") {
      return undefined;
    }
    return groupOptions.find((opt) => opt.value === mode.userGroupId);
  }, [action.mode, groupOptions]);

  const handleModeChange = (newMode: ModeTag) => {
    if (newMode === action.mode._tag) {
      return;
    }
    switch (newMode) {
      case "specific_users":
        onChange({
          ...action,
          mode: { _tag: "specific_users", mongoUserIds: [] },
        });
        break;
      case "user_group":
        onChange({ ...action, mode: { _tag: "user_group", userGroupId: "" } });
        break;
      case "actor":
        onChange({
          ...action,
          mode: { _tag: "actor", assigneeTypes: ["USER"] },
        });
        break;
      case "unassign":
        onChange({ ...action, mode: { _tag: "unassign" } });
        break;
      default:
        assertNever(newMode);
    }
  };

  const selectedActorAssigneeTypes: BadgeRedoListItem<AssignV3ActorAssigneeType>[] =
    useMemo(() => {
      if (action.mode._tag !== "actor") {
        return [];
      }
      const selected = new Set(action.mode.assigneeTypes);
      return actorAssigneeTypeOptions.filter((opt) => selected.has(opt.value));
    }, [action.mode]);

  const [actorDropdownButtonRef, setActorDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [actorDropdownOpen, setActorDropdownOpen] = useState(false);

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<ModeTag>
        disabled={!modeEditable}
        fitToAnchor
        label="Assign to"
        options={modeOptions}
        optionSelected={({ item }) => handleModeChange(item.value)}
        selectedItem={selectedModeOption}
      />

      {action.mode._tag === "specific_users" && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Users ({selectedUsers.length})
          </Text>
          <RedoTagsDropdownInputButton
            disabled={!modeEditable}
            isDropdownOpen={dropdownOpen}
            onClick={() => modeEditable && setDropdownOpen(!dropdownOpen)}
            placeholder="Select users..."
            ref={setDropdownButtonRef}
            removeItem={(item) => {
              if (!modeEditable || action.mode._tag !== "specific_users") {
                return;
              }
              onChange({
                ...action,
                mode: {
                  _tag: "specific_users",
                  mongoUserIds: action.mode.mongoUserIds.filter(
                    (id) => id !== item.value,
                  ),
                },
              });
            }}
            selectedItems={selectedUsers}
            size="xs"
          />
          <RedoMultiselectDropdown<string>
            dropdownAnchor={dropdownButtonRef}
            dropdownOpen={dropdownOpen}
            fitToAnchor
            items={userOptions}
            selectedItems={selectedUsers}
            selectionVariant="checkbox"
            setDropdownOpen={setDropdownOpen}
            setSelectedItems={(items) => {
              if (!modeEditable) {
                return;
              }
              onChange({
                ...action,
                mode: {
                  _tag: "specific_users",
                  mongoUserIds: items.map((item) => item.value),
                },
              });
            }}
            size="xs"
          />
        </Flex>
      )}

      {action.mode._tag === "user_group" && (
        <RedoSingleSelectDropdownInput<string>
          disabled={!modeEditable}
          fitToAnchor
          label="User group"
          options={groupOptions}
          optionSelected={({ item }) => {
            onChange({
              ...action,
              mode: { _tag: "user_group", userGroupId: item.value },
            });
          }}
          placeholder={
            userGroupsLoad.pending ? "Loading groups..." : "Select a group..."
          }
          selectedItem={selectedGroupOption}
        />
      )}

      {action.mode._tag === "actor" && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Assignee types ({selectedActorAssigneeTypes.length})
          </Text>
          <RedoTagsDropdownInputButton
            disabled={!modeEditable}
            isDropdownOpen={actorDropdownOpen}
            onClick={() =>
              modeEditable && setActorDropdownOpen(!actorDropdownOpen)
            }
            placeholder="Select assignee types..."
            ref={setActorDropdownButtonRef}
            removeItem={(item) => {
              if (!modeEditable || action.mode._tag !== "actor") {
                return;
              }
              onChange({
                ...action,
                mode: {
                  _tag: "actor",
                  assigneeTypes: action.mode.assigneeTypes.filter(
                    (type) => type !== item.value,
                  ),
                },
              });
            }}
            selectedItems={selectedActorAssigneeTypes}
            size="xs"
          />
          <RedoMultiselectDropdown<AssignV3ActorAssigneeType>
            dropdownAnchor={actorDropdownButtonRef}
            dropdownOpen={actorDropdownOpen}
            fitToAnchor
            items={actorAssigneeTypeOptions}
            selectedItems={selectedActorAssigneeTypes}
            selectionVariant="checkbox"
            setDropdownOpen={setActorDropdownOpen}
            setSelectedItems={(items) => {
              if (!modeEditable) {
                return;
              }
              onChange({
                ...action,
                mode: {
                  _tag: "actor",
                  assigneeTypes: items.map((item) => item.value),
                },
              });
            }}
            size="xs"
          />
          <Text fontSize="xs" textColor="secondary">
            Conversation will be assigned to whoever fired the trigger when
            their type is in this list.
          </Text>
        </Flex>
      )}

      {action.mode._tag !== "unassign" && action.mode._tag !== "actor" && (
        <>
          <RedoSingleSelectDropdownInput<AssignV3Strategy>
            disabled={!strategyEditable}
            fitToAnchor
            label="Strategy"
            options={strategyOptions}
            optionSelected={({ item }) => {
              onChange({ ...action, strategy: item.value });
            }}
            selectedItem={selectedStrategyOption}
          />

          <Flex dir="column" gap="xs">
            <RedoTextInput
              description="Optional. Users at or above this many open tickets are skipped. Leave blank for no cap."
              inputTypeHint="number"
              label="Max open tickets per user"
              min={1}
              placeholder="No cap"
              setValue={(value) => {
                const trimmed = value.trim();
                if (trimmed === "") {
                  onChange({ ...action, maxTicketsPerUser: null });
                  return;
                }
                const parsed = Number.parseInt(trimmed, 10);
                if (Number.isFinite(parsed) && parsed > 0) {
                  onChange({ ...action, maxTicketsPerUser: parsed });
                }
              }}
              state={maxTicketsEditable ? "default" : "readonly"}
              value={
                action.maxTicketsPerUser == null
                  ? ""
                  : String(action.maxTicketsPerUser)
              }
            />
          </Flex>
        </>
      )}
    </Flex>
  );
});
